/**
 * Geocode a location name to coordinates using the free Nominatim API.
 * Rate-limited to 1 request per second (Nominatim policy).
 */

export interface GeocodingResult {
  lat: number;
  lon: number;
  displayName: string;
}

let lastRequestTime = 0;

export async function searchLocations(
  query: string,
): Promise<GeocodingResult[]> {
  if (!query || query.trim().length < 2) return [];

  // Enforce 1-second rate limit
  const now = Date.now();
  const wait = Math.max(0, 1000 - (now - lastRequestTime));
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  lastRequestTime = Date.now();

  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&accept-language=en&addressdetails=1`;

  const response = await fetch(url);
  if (!response.ok) throw new Error("Geocoding request failed");

  const data = await response.json();

  return data.map((item: any) => ({
    lat: parseFloat(item.lat),
    lon: parseFloat(item.lon),
    displayName: buildShortName(item.address),
  }));
}

/**
 * Reverse-geocode coordinates to a short location name (Nominatim).
 * Returns null on failure so callers can fall back to the raw coordinates.
 */
export async function reverseGeocode(
  lat: number,
  lon: number,
): Promise<GeocodingResult | null> {
  const now = Date.now();
  const wait = Math.max(0, 1000 - (now - lastRequestTime));
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  lastRequestTime = Date.now();

  const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=en&addressdetails=1`;

  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = await response.json();
    const displayName = buildShortName(data.address) || data.display_name;
    if (!displayName) return null;
    return { lat, lon, displayName };
  } catch {
    return null;
  }
}

/**
 * Build a short human-readable name from Nominatim address parts.
 * E.g. "Karaj, Iran" instead of "Karaj, Karaj Central District, Alborz Province, Iran"
 */
function buildShortName(address: any): string {
  if (!address) return "";

  // Pick the most specific locality, preferring city > town > village > hamlet
  const locality =
    address.city ||
    address.town ||
    address.village ||
    address.hamlet ||
    address.municipality ||
    address.county ||
    address.state;

  const country = address.country || "";

  if (locality && country) {
    return `${locality}, ${country}`;
  }
  if (locality) return locality;
  if (country) return country;
  return "";
}
